// File: src/App.js
// Redesigned UTM AI Knowledge Base Search Interface
// Features: Dark mode, clickable examples, search history, thumbs feedback, mobile responsive
// 🎮 Hidden Easter Egg: Try searching "do we service space aliens?"

import React, { useState, useEffect, useRef, useCallback } from 'react';
import logo from './logo.svg';
const API_BASE =
  (process.env.REACT_APP_API_BASE ? process.env.REACT_APP_API_BASE.replace(/\/$/, "") : "") || "";
const QUERY_ENDPOINT = `${API_BASE}/query`;
const AUTHORIZE_ENDPOINT = `${API_BASE}/authorize`;

// Auth session keys (sessionStorage: cleared when the tab closes)
const AUTH_TOKEN_KEY = 'utm_auth_token';
// Stores the absolute epoch-ms timestamp when the lockout ends, NOT a boolean.
// A boolean cannot survive a page refresh usefully: the remaining seconds would
// be lost, leaving the UI locked with no running countdown and no way back.
const AUTH_LOCKED_UNTIL_KEY = 'utm_auth_locked_until';

// Seconds remaining on a stored lockout, or 0 if none / already expired.
function storedLockRemaining() {
  try {
    const until = Number(sessionStorage.getItem(AUTH_LOCKED_UNTIL_KEY) || 0);
    if (!until) return 0;
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  } catch {
    return 0;
  }
}
// Fallback if the server 403 somehow omits remaining_seconds. Keep in sync with
// backend LOCKOUT_SECONDS (5 min).
const LOCKOUT_FALLBACK_SECONDS = 5 * 60;

// Stable per-device id sent as X-Client-Id so the backend can scope its lockout
// counter per browser instead of per public IP. Without this, everyone behind a
// shared office IP shares one counter and one person's bad attempts lock out the
// whole office.
//
// Deliberately localStorage (not sessionStorage): this identifies the device and
// should persist across tabs and restarts. The auth *token* stays in
// sessionStorage so it still clears when the tab closes.
const CLIENT_ID_STORAGE_KEY = 'utm_client_id';

function getClientId() {
  try {
    let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (!clientId) {
      clientId =
        (typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
    }
    return clientId;
  } catch {
    // Private mode / storage disabled: fall back to a per-load id. Lockout then
    // degrades to per-IP for this user, which is the old behavior, not a break.
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// Local storage keys
const STORAGE_KEYS = {
  SEARCH_HISTORY: 'utm_kb_search_history',
  DARK_MODE: 'utm_kb_dark_mode',
  FEEDBACK_STATS: 'utm_kb_feedback_stats'
};

// 🎮 Secret easter egg query
const SECRET_QUERY = 'do we service space aliens?';

// 👾 Space Invaders Game Component
function SpaceInvaders({ onClose, darkMode }) {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [restartKey, setRestartKey] = useState(0); // Forces useEffect restart
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('utm_space_invaders_high');
    return saved ? parseInt(saved, 10) : 0;
  });

  const startGame = useCallback(() => {
    setScore(0);
    setGameOver(false);
    setRestartKey(k => k + 1); // Increment to trigger useEffect restart
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    // Game state
    const game = {
      player: { x: W / 2 - 20, y: H - 40, w: 40, h: 20, speed: 5 },
      bullets: [],
      aliens: [],
      alienBullets: [],
      alienDir: 1,
      alienSpeed: 1,
      keys: {},
      running: true,
      score: 0,
      reset() {
        this.player.x = W / 2 - 20;
        this.bullets = [];
        this.alienBullets = [];
        this.score = 0;
        this.alienDir = 1;
        this.alienSpeed = 1;
        this.running = true;
        this.initAliens();
      },
      initAliens() {
        this.aliens = [];
        for (let row = 0; row < 4; row++) {
          for (let col = 0; col < 8; col++) {
            this.aliens.push({
              x: 30 + col * 45,
              y: 30 + row * 35,
              w: 30,
              h: 20,
              alive: true
            });
          }
        }
      }
    };
    game.initAliens();
    gameRef.current = game;

    // Input handlers
    const onKeyDown = (e) => {
      game.keys[e.key] = true;
      if (e.key === ' ' && game.running) {
        e.preventDefault();
        game.bullets.push({ x: game.player.x + game.player.w / 2 - 2, y: game.player.y, w: 4, h: 10 });
      }
    };
    const onKeyUp = (e) => { game.keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    // Game loop
    let animId;
    const loop = () => {
      if (!game.running) return;

      // Clear
      ctx.fillStyle = darkMode ? '#0f172a' : '#1a1a2e';
      ctx.fillRect(0, 0, W, H);

      // Stars background
      ctx.fillStyle = '#ffffff33';
      for (let i = 0; i < 50; i++) {
        ctx.fillRect((i * 37) % W, (i * 53) % H, 2, 2);
      }

      // Move player
      if (game.keys['ArrowLeft'] || game.keys['a']) game.player.x -= game.player.speed;
      if (game.keys['ArrowRight'] || game.keys['d']) game.player.x += game.player.speed;
      game.player.x = Math.max(0, Math.min(W - game.player.w, game.player.x));

      // Draw player (ship)
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.moveTo(game.player.x + game.player.w / 2, game.player.y);
      ctx.lineTo(game.player.x, game.player.y + game.player.h);
      ctx.lineTo(game.player.x + game.player.w, game.player.y + game.player.h);
      ctx.closePath();
      ctx.fill();

      // Move bullets
      game.bullets = game.bullets.filter(b => {
        b.y -= 8;
        return b.y > 0;
      });

      // Draw bullets
      ctx.fillStyle = '#fbbf24';
      game.bullets.forEach(b => ctx.fillRect(b.x, b.y, b.w, b.h));

      // Move aliens
      let hitEdge = false;
      game.aliens.forEach(a => {
        if (a.alive) {
          a.x += game.alienDir * game.alienSpeed;
          if (a.x <= 0 || a.x + a.w >= W) hitEdge = true;
        }
      });
      if (hitEdge) {
        game.alienDir *= -1;
        game.aliens.forEach(a => { a.y += 15; });
      }

      // Draw aliens
      game.aliens.forEach(a => {
        if (a.alive) {
          ctx.fillStyle = '#ef4444';
          ctx.fillRect(a.x, a.y, a.w, a.h);
          // Eyes
          ctx.fillStyle = '#fff';
          ctx.fillRect(a.x + 5, a.y + 5, 6, 6);
          ctx.fillRect(a.x + 19, a.y + 5, 6, 6);
          ctx.fillStyle = '#000';
          ctx.fillRect(a.x + 7, a.y + 7, 3, 3);
          ctx.fillRect(a.x + 21, a.y + 7, 3, 3);
        }
      });

      // Alien shooting
      if (Math.random() < 0.02) {
        const alive = game.aliens.filter(a => a.alive);
        if (alive.length) {
          const shooter = alive[Math.floor(Math.random() * alive.length)];
          game.alienBullets.push({ x: shooter.x + shooter.w / 2 - 2, y: shooter.y + shooter.h, w: 4, h: 10 });
        }
      }

      // Move alien bullets
      game.alienBullets = game.alienBullets.filter(b => {
        b.y += 5;
        return b.y < H;
      });

      // Draw alien bullets
      ctx.fillStyle = '#f87171';
      game.alienBullets.forEach(b => ctx.fillRect(b.x, b.y, b.w, b.h));

      // Collision: bullets vs aliens
      game.bullets.forEach(b => {
        game.aliens.forEach(a => {
          if (a.alive && b.x < a.x + a.w && b.x + b.w > a.x && b.y < a.y + a.h && b.y + b.h > a.y) {
            a.alive = false;
            b.y = -100;
            game.score += 10;
            setScore(game.score);
          }
        });
      });

      // Collision: alien bullets vs player
      game.alienBullets.forEach(b => {
        if (b.x < game.player.x + game.player.w && b.x + b.w > game.player.x &&
          b.y < game.player.y + game.player.h && b.y + b.h > game.player.y) {
          game.running = false;
          setGameOver(true);
          if (game.score > highScore) {
            setHighScore(game.score);
            localStorage.setItem('utm_space_invaders_high', game.score.toString());
          }
        }
      });

      // Aliens reach bottom
      if (game.aliens.some(a => a.alive && a.y + a.h >= game.player.y)) {
        game.running = false;
        setGameOver(true);
        if (game.score > highScore) {
          setHighScore(game.score);
          localStorage.setItem('utm_space_invaders_high', game.score.toString());
        }
      }

      // Win check
      if (game.aliens.every(a => !a.alive)) {
        game.alienSpeed += 0.5;
        game.initAliens();
      }

      // Score display
      ctx.fillStyle = '#fff';
      ctx.font = '16px JetBrains Mono, monospace';
      ctx.fillText(`Score: ${game.score}`, 10, 20);
      ctx.fillText(`High: ${Math.max(game.score, highScore)}`, W - 100, 20);

      animId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [darkMode, highScore, restartKey]);

  return (
    <div style={{
      background: darkMode ? '#1e293b' : '#fff',
      borderRadius: 16,
      padding: 20,
      textAlign: 'center'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: darkMode ? '#f1f5f9' : '#0f172a', fontSize: 18 }}>
          👾 Space Invaders 👾
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: '1px solid ' + (darkMode ? '#334155' : '#e2e8f0'),
            borderRadius: 8,
            padding: '6px 12px',
            cursor: 'pointer',
            color: darkMode ? '#94a3b8' : '#64748b'
          }}
        >
          ✕ Close
        </button>
      </div>

      <canvas
        ref={canvasRef}
        width={400}
        height={350}
        style={{
          border: '2px solid ' + (darkMode ? '#334155' : '#e2e8f0'),
          borderRadius: 8,
          maxWidth: '100%'
        }}
      />

      {gameOver && (
        <div style={{ marginTop: 16 }}>
          <p style={{ color: '#ef4444', fontWeight: 600, fontSize: 18, margin: '0 0 12px' }}>
            Game Over! Score: {score}
          </p>
          <button
            onClick={startGame}
            style={{
              background: '#22c55e',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Play Again
          </button>
        </div>
      )}

      {/* Mobile Touch Controls */}
      <div style={{ marginTop: 16 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          marginBottom: 8
        }}>
          <button
            onTouchStart={(e) => { e.preventDefault(); if (gameRef.current) gameRef.current.keys['ArrowLeft'] = true; }}
            onTouchEnd={(e) => { e.preventDefault(); if (gameRef.current) gameRef.current.keys['ArrowLeft'] = false; }}
            onMouseDown={() => { if (gameRef.current) gameRef.current.keys['ArrowLeft'] = true; }}
            onMouseUp={() => { if (gameRef.current) gameRef.current.keys['ArrowLeft'] = false; }}
            onMouseLeave={() => { if (gameRef.current) gameRef.current.keys['ArrowLeft'] = false; }}
            style={{
              width: 60, height: 60,
              borderRadius: 12,
              border: '2px solid ' + (darkMode ? '#334155' : '#e2e8f0'),
              background: darkMode ? '#1e293b' : '#f8fafc',
              color: darkMode ? '#f1f5f9' : '#0f172a',
              fontSize: 24,
              cursor: 'pointer',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'manipulation'
            }}
            aria-label="Move Left"
          >
            ◀
          </button>

          <button
            onTouchStart={(e) => {
              e.preventDefault();
              if (gameRef.current && gameRef.current.running) {
                gameRef.current.bullets.push({
                  x: gameRef.current.player.x + gameRef.current.player.w / 2 - 2,
                  y: gameRef.current.player.y,
                  w: 4,
                  h: 10
                });
              }
            }}
            onMouseDown={() => {
              if (gameRef.current && gameRef.current.running) {
                gameRef.current.bullets.push({
                  x: gameRef.current.player.x + gameRef.current.player.w / 2 - 2,
                  y: gameRef.current.player.y,
                  w: 4,
                  h: 10
                });
              }
            }}
            style={{
              width: 80, height: 60,
              borderRadius: 12,
              border: '2px solid #ef4444',
              background: '#ef4444',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'manipulation'
            }}
            aria-label="Fire"
          >
            FIRE 🔥
          </button>

          <button
            onTouchStart={(e) => { e.preventDefault(); if (gameRef.current) gameRef.current.keys['ArrowRight'] = true; }}
            onTouchEnd={(e) => { e.preventDefault(); if (gameRef.current) gameRef.current.keys['ArrowRight'] = false; }}
            onMouseDown={() => { if (gameRef.current) gameRef.current.keys['ArrowRight'] = true; }}
            onMouseUp={() => { if (gameRef.current) gameRef.current.keys['ArrowRight'] = false; }}
            onMouseLeave={() => { if (gameRef.current) gameRef.current.keys['ArrowRight'] = false; }}
            style={{
              width: 60, height: 60,
              borderRadius: 12,
              border: '2px solid ' + (darkMode ? '#334155' : '#e2e8f0'),
              background: darkMode ? '#1e293b' : '#f8fafc',
              color: darkMode ? '#f1f5f9' : '#0f172a',
              fontSize: 24,
              cursor: 'pointer',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'manipulation'
            }}
            aria-label="Move Right"
          >
            ▶
          </button>
        </div>

        <p style={{ color: darkMode ? '#64748b' : '#94a3b8', fontSize: 12, margin: 0 }}>
          Tap buttons above or use ← → / A D + SPACE on keyboard
        </p>
      </div>
    </div>
  );
}

/**
 * The backend returns one source entry per chunk placed in the model's context,
 * not per chunk the model actually used. Rendering all of them means a refusal
 * ("I don't know.") still ships with a full, authoritative-looking source list —
 * which reads as though retrieval succeeded and only the answer failed.
 *
 * Show only the [n] markers that appear in the answer text.
 */
function pickCitedSources(answerText, sourcesMap) {
  const text = typeof answerText === 'string' ? answerText : '';
  const cited = new Set();
  const re = /\[(\d+)\]/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    cited.add(m[1]);
  }
  return Object.entries(sourcesMap || {}).filter(([key]) => cited.has(key));
}

export default function App() {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState({});
  const [errMsg, setErrMsg] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState({ helpful: 0, total: 0 });
  const [currentFeedback, setCurrentFeedback] = useState(null); // 'up' | 'down' | null
  const [showGame, setShowGame] = useState(false); // 🎮 Easter egg game
  const inputRef = useRef(null);

  // -------------------- Auth state --------------------
  const [authToken, setAuthToken] = useState(
    () => sessionStorage.getItem(AUTH_TOKEN_KEY) || ''
  );
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [authTries, setAuthTries] = useState(0);
  const [authLocked, setAuthLocked] = useState(() => storedLockRemaining() > 0);
  // Lockout is backend-authoritative: the server tells us remaining_seconds and
  // we count down locally purely for display, then re-show the form at zero.
  // Rehydrated from storage so a page refresh resumes the countdown instead of
  // stranding the user on a locked screen with no timer.
  const [lockRemaining, setLockRemaining] = useState(() => storedLockRemaining());
  const [authLoading, setAuthLoading] = useState(false);
  const authorized = Boolean(authToken) && !authLocked;

  // Countdown driven by the server's remaining_seconds. Ticks once/sec; when it
  // hits zero the lockout clears and the login form comes back automatically.
  useEffect(() => {
    if (!authLocked) return;

    // Self-healing: locked but nothing left to wait out (expired timer, restored
    // state, clock change). Release rather than sitting on a dead screen.
    if (lockRemaining <= 0) {
      try { sessionStorage.removeItem(AUTH_LOCKED_UNTIL_KEY); } catch { /* ignore */ }
      setAuthLocked(false);
      setAuthTries(0);
      setAuthError('');
      return;
    }

    const t = setInterval(() => {
      setLockRemaining((s) => {
        if (s <= 1) {
          clearInterval(t);
          try { sessionStorage.removeItem(AUTH_LOCKED_UNTIL_KEY); } catch { /* ignore */ }
          setAuthLocked(false);
          setAuthTries(0);
          setAuthError('');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [authLocked, lockRemaining]);

  // Example questions for quick start
  const exampleQuestions = [
    { text: 'Do Case Managers get alerts?', icon: '🔔' },
    { text: 'How do I reset my password?', icon: '🔑' },
    { text: 'What are the permission levels?', icon: '👤' },
    { text: 'How to export patient data?', icon: '📊' },
  ];

  // Load persisted state on mount
  useEffect(() => {
    // Load search history
    try {
      const savedHistory = localStorage.getItem(STORAGE_KEYS.SEARCH_HISTORY);
      if (savedHistory) setRecentSearches(JSON.parse(savedHistory));
    } catch (e) { console.warn('Failed to load search history', e); }

    // Load dark mode preference
    try {
      const savedDarkMode = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
      if (savedDarkMode) setDarkMode(JSON.parse(savedDarkMode));
    } catch (e) { console.warn('Failed to load dark mode preference', e); }

    // Load feedback stats
    try {
      const savedStats = localStorage.getItem(STORAGE_KEYS.FEEDBACK_STATS);
      if (savedStats) setFeedbackStats(JSON.parse(savedStats));
    } catch (e) { console.warn('Failed to load feedback stats', e); }
  }, []);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DARK_MODE, JSON.stringify(darkMode));
  }, [darkMode]);

  // Save feedback stats
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FEEDBACK_STATS, JSON.stringify(feedbackStats));
  }, [feedbackStats]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowHistory(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    // 🎮 Easter egg check
    if (query.trim().toLowerCase() === SECRET_QUERY) {
      setShowGame(true);
      setAnswer('');
      setIsSearching(false);
      setShowHistory(false);
      return;
    }

    setShowGame(false);
    setIsSearching(true);
    setShowHistory(false);
    setErrMsg('');
    setAnswer('');
    setSources({});
    setCurrentFeedback(null);

    // Add to recent searches
    const updatedHistory = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updatedHistory);
    localStorage.setItem(STORAGE_KEYS.SEARCH_HISTORY, JSON.stringify(updatedHistory));

    try {
      const res = await fetch(QUERY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ query }),
      });

      if (res.status === 401) {
        // Token expired or invalid -> drop it and force re-auth.
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        setAuthToken('');
        throw new Error('Session expired. Please authorize again.');
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} - ${text || 'Request failed'}`);
      }

      const data = await res.json();
      setAnswer(typeof data?.answer === 'string' ? data.answer : String(data ?? ''));
      setSources(data?.sources || {});
    } catch (err) {
      console.error(err);
      setErrMsg(err?.message || 'Error occurred. See console.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleExampleClick = (question) => {
    setQuery(question);
    inputRef.current?.focus();
  };

  const handleClear = () => {
    setQuery('');
    setAnswer('');
    setSources({});
    setErrMsg('');
    setCurrentFeedback(null);
    setShowGame(false);
    inputRef.current?.focus();
  };

  // -------------------- Auth handler --------------------
  const handleAuthorize = async (e) => {
    e.preventDefault();
    if (!authCode.trim() || authLocked) return;

    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await fetch(AUTHORIZE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Id': getClientId(),
        },
        body: JSON.stringify({ code: authCode.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.authorized && data.token) {
        sessionStorage.setItem(AUTH_TOKEN_KEY, data.token);
        sessionStorage.removeItem(AUTH_LOCKED_UNTIL_KEY);
        setAuthToken(data.token);
        setAuthCode('');
        setAuthTries(0);
        setAuthLocked(false);
        setLockRemaining(0);
        setAuthError('');
        return;
      }

      // detail may be a plain string OR a structured lockout object from the
      // backend ({ message, remaining_seconds, ... }). Normalize both.
      const detail = data?.detail;
      const detailObj =
        detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : {};
      const detailMsg = typeof detail === 'string' ? detail : detailObj.message || '';

      setAuthCode('');

      // Backend-authoritative lockout: 403 means we're locked, and the server
      // tells us for how long. Trust remaining_seconds over any local counter.
      if (response.status === 403) {
        const raw = Number(detailObj.remaining_seconds || 0);
        const remaining = raw > 0 ? raw : LOCKOUT_FALLBACK_SECONDS;
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        // Persist WHEN the lockout ends, so a refresh can resume the countdown.
        sessionStorage.setItem(
          AUTH_LOCKED_UNTIL_KEY,
          String(Date.now() + remaining * 1000)
        );
        setAuthToken('');
        setAuthLocked(true);
        setLockRemaining(remaining);
        setAuthError(detailMsg || 'Too many incorrect attempts. Temporarily locked.');
        return;
      }

      // 401: still have attempts left; show the server's remaining count.
      const nextTries = authTries + 1;
      setAuthTries(nextTries);
      setAuthError(detailMsg || `Invalid authorization code. ${3 - nextTries} attempt(s) remaining.`);
    } catch (err) {
      console.error(err);
      setAuthError('Authorization server error. See console.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleHistoryClick = (search) => {
    setQuery(search);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  const handleFeedback = (type) => {
    if (currentFeedback === type) return; // Already selected

    setFeedbackStats(prev => {
      const newStats = { ...prev };

      // If changing from one type to another
      if (currentFeedback === 'up' && type === 'down') {
        newStats.helpful = Math.max(0, newStats.helpful - 1);
      } else if (currentFeedback === 'down' && type === 'up') {
        newStats.helpful += 1;
      } else if (!currentFeedback) {
        // Fresh feedback
        newStats.total += 1;
        if (type === 'up') newStats.helpful += 1;
      }

      return newStats;
    });

    setCurrentFeedback(type);
  };

  const helpfulPercentage = feedbackStats.total > 0
    ? Math.round((feedbackStats.helpful / feedbackStats.total) * 100)
    : 0;

  return (
    <div className={darkMode ? 'dark' : ''}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        
        * {
          box-sizing: border-box;
          font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
        }
        
        body { margin: 0; }
        
        .mono {
          font-family: 'JetBrains Mono', monospace;
        }
        
        /* Light mode */
        :root {
          --bg-primary: #f8fafc;
          --bg-secondary: #ffffff;
          --bg-accent: linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%);
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --text-muted: #94a3b8;
          --border: #e2e8f0;
          --accent: #0ea5e9;
          --accent-hover: #0284c7;
          --success: #22c55e;
          --danger: #ef4444;
          --card-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 40px -3px rgba(0, 0, 0, 0.08);
        }
        
        /* Dark mode */
        .dark {
          --bg-primary: #0f172a;
          --bg-secondary: #1e293b;
          --bg-accent: linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #0369a1 100%);
          --text-primary: #f1f5f9;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --border: #334155;
          --accent: #38bdf8;
          --accent-hover: #0ea5e9;
          --success: #4ade80;
          --danger: #f87171;
          --card-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 10px 40px -3px rgba(0, 0, 0, 0.4);
        }
        
        .app-bg {
          background: var(--bg-primary);
          min-height: 100vh;
        }
        
        .hero-gradient {
          background: var(--bg-accent);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 280px;
          z-index: 0;
        }
        
        .hero-gradient::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 80px;
          background: linear-gradient(to bottom, transparent, var(--bg-primary));
        }
        
        .card {
          background: var(--bg-secondary);
          border: 1px solid var(--border);
          box-shadow: var(--card-shadow);
        }
        
        .search-input {
          background: var(--bg-primary);
          border: 2px solid var(--border);
          color: var(--text-primary);
          transition: all 0.2s ease;
        }
        
        .search-input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
          outline: none;
        }
        
        .search-input::placeholder {
          color: var(--text-muted);
        }
        
        .btn-primary {
          background: var(--accent);
          color: white;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: var(--accent-hover);
          transform: translateY(-1px);
        }
        
        .btn-primary:active {
          transform: translateY(0);
        }
        
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .btn-secondary {
          background: transparent;
          color: var(--text-secondary);
          border: 1px solid var(--border);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .btn-secondary:hover {
          background: var(--bg-primary);
          border-color: var(--text-muted);
          color: var(--text-primary);
        }
        
        .example-btn {
          background: var(--bg-primary);
          border: 1px solid var(--border);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .example-btn:hover {
          border-color: var(--accent);
          color: var(--accent);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
        }
        
        .kbd {
          background: var(--bg-primary);
          border: 1px solid var(--border);
          color: var(--text-muted);
          font-family: 'JetBrains Mono', monospace;
        }
        
        .text-primary { color: var(--text-primary); }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-accent { color: var(--accent); }
        .border-color { border-color: var(--border); }
        .bg-primary { background: var(--bg-primary); }
        .bg-secondary { background: var(--bg-secondary); }
        
        .error-box {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--danger);
          color: var(--danger);
        }
        
        .answer-pre {
          margin: 0;
          white-space: pre-wrap;
          line-height: 1.7;
          font-size: 15px;
          color: var(--text-primary);
        }
        
        .feedback-btn {
          background: var(--bg-primary);
          border: 1px solid var(--border);
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .feedback-btn:hover {
          border-color: var(--accent);
        }
        
        .feedback-btn.active-up {
          background: rgba(34, 197, 94, 0.15);
          border-color: var(--success);
          color: var(--success);
        }
        
        .feedback-btn.active-down {
          background: rgba(239, 68, 68, 0.15);
          border-color: var(--danger);
          color: var(--danger);
        }
        
        .source-link {
          color: var(--accent);
          text-decoration: none;
          font-size: 13px;
        }
        
        .source-link:hover {
          text-decoration: underline;
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.4s ease-out forwards;
        }
        
        .spinner {
          border: 3px solid var(--border);
          border-top-color: var(--accent);
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Responsive styles */
        .container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 16px;
        }
        
        .header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }
        
        .search-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .search-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .input-wrapper {
          flex: 1;
          min-width: 200px;
          position: relative;
        }
        
        .button-group {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }
        
        .examples-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 12px;
        }
        
        @media (max-width: 640px) {
          .header-inner {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .header-right {
            width: 100%;
            justify-content: flex-end;
          }
          
          .hero-gradient {
            height: 220px;
          }
          
          .search-row {
            flex-direction: column;
          }
          
          .button-group {
            width: 100%;
            justify-content: stretch;
          }
          
          .button-group button {
            flex: 1;
          }
          
          .examples-grid {
            grid-template-columns: 1fr;
          }
          
          .answer-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          
          .feedback-section {
            width: 100%;
            justify-content: space-between;
          }
        }

        /* -------------------- Auth overlay -------------------- */
        .auth-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(15, 23, 42, 0.72);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          backdrop-filter: blur(4px);
        }
        .auth-box {
          width: 100%;
          max-width: 420px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 28px;
          box-shadow: var(--card-shadow);
          text-align: center;
        }
        .auth-box h2 { margin: 0 0 8px; color: var(--text-primary); font-size: 20px; }
        .auth-box p { margin: 0 0 18px; color: var(--text-secondary); font-size: 14px; }
        .auth-input {
          width: 100%;
          height: 46px;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0 12px;
          font-size: 16px;
          margin-bottom: 12px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }
        .auth-input:focus { outline: none; border-color: var(--accent); }
        .auth-submit {
          width: 100%;
          height: 46px;
          border: 0;
          border-radius: 12px;
          font-weight: 600;
          font-size: 15px;
          color: #fff;
          background: var(--accent);
          cursor: pointer;
        }
        .auth-submit:hover { background: var(--accent-hover); }
        .auth-submit:disabled { opacity: .6; cursor: not-allowed; }
        .auth-error {
          margin-top: 12px;
          color: #991b1b;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 8px 10px;
          font-size: 13px;
        }
        .auth-note { margin-top: 12px; font-size: 13px; color: var(--text-muted); }
        .auth-countdown {
          margin-top: 14px;
          font-size: 24px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          color: var(--text-primary);
        }
      `}</style>

      <div className="app-bg" style={{ position: 'relative', minHeight: '100vh' }}>
        {/* Background Gradient */}
        <div className="hero-gradient" />

        {/* -------------------- Auth gate -------------------- */}
        {!authorized && (
          <div className="auth-overlay">
            <div className="auth-box">
              {!authLocked ? (
                <form onSubmit={handleAuthorize}>
                  <h2>UTM AI Knowledge Base</h2>
                  <p>Authorization required</p>
                  <input
                    className="auth-input"
                    type="password"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="Enter authorization code"
                    autoFocus
                  />
                  <button className="auth-submit" type="submit" disabled={authLoading}>
                    {authLoading ? 'Authorizing…' : 'Authorize'}
                  </button>
                  {authError && <div className="auth-error">{authError}</div>}
                  <div className="auth-note">Authorized users only.</div>
                </form>
              ) : (
                <>
                  <h2>Temporarily Locked</h2>
                  <p>Too many incorrect attempts.</p>
                  {lockRemaining > 0 ? (
                    <div className="auth-countdown">
                      Try again in{' '}
                      {Math.floor(lockRemaining / 60)}:
                      {String(lockRemaining % 60).padStart(2, '0')}
                    </div>
                  ) : (
                    <p>Unlocking…</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <header style={{ position: 'relative', zIndex: 10, padding: '16px' }}>
          <div className="container">
            <div className="header-inner">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={logo}
                alt="UTM Healthcare"
                style={{
                height: 32,
                width: 'auto',
                display: 'block'
                }}
                />
  <div>
    <h1 style={{ margin: 0, color: 'white', fontWeight: 600, fontSize: 18 }}>
      UTM AI Knowledge Base
    </h1>
    <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
      Ask questions across your documents
    </p>
  </div>
</div>

              <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Helpful percentage badge */}
                {feedbackStats.total > 0 && (
                  <div style={{
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                    borderRadius: 20, padding: '6px 12px',
                    color: 'white', fontSize: 12, fontWeight: 500
                  }}>
                    {helpfulPercentage}% Helpful
                  </div>
                )}

                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, transition: 'background 0.2s'
                  }}
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? '☀️' : '🌙'}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main style={{ position: 'relative', zIndex: 10, padding: '32px 16px 48px' }}>
          <div className="container">

            {/* Search Card */}
            <div className="card animate-fade-in-up" style={{ borderRadius: 16, padding: 24, marginBottom: 24 }}>
              <h2 className="text-primary" style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 600 }}>
                Ask anything
              </h2>

              <form onSubmit={handleSearch} className="search-form">
                <div className="search-row">
                  <div className="input-wrapper">
                    <span style={{
                      position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                      color: 'var(--text-muted)', fontSize: 18
                    }}>🔍</span>

                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => setShowHistory(true)}
                      placeholder="Search documentation, guides, and FAQs..."
                      className="search-input"
                      style={{
                        width: '100%', padding: '14px 14px 14px 44px',
                        borderRadius: 12, fontSize: 15
                      }}
                      autoComplete="off"
                      aria-label="Search query"
                    />

                    <span className="kbd" style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      padding: '3px 8px', borderRadius: 6, fontSize: 11
                    }}>/</span>

                    {/* Recent Searches Dropdown */}
                    {showHistory && recentSearches.length > 0 && !answer && (
                      <div className="card" style={{
                        position: 'absolute', top: 'calc(100% + 8px)',
                        left: 0, right: 0, borderRadius: 12, padding: 8, zIndex: 20
                      }}>
                        <div className="text-muted" style={{ fontSize: 11, fontWeight: 500, padding: '8px 12px' }}>
                          Recent searches
                        </div>
                        {recentSearches.map((search, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleHistoryClick(search)}
                            className="text-secondary"
                            style={{
                              width: '100%', textAlign: 'left', padding: '10px 12px',
                              borderRadius: 8, border: 'none', background: 'transparent',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                              transition: 'background 0.15s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'var(--bg-primary)'}
                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: 14 }}>🕐</span>
                            {search}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="button-group">
                    {query && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="btn-secondary"
                        style={{ padding: '12px 16px', borderRadius: 10, fontWeight: 500, fontSize: 14 }}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={isSearching || !query.trim()}
                      className="btn-primary"
                      style={{
                        padding: '12px 20px', borderRadius: 10,
                        fontWeight: 500, fontSize: 14,
                        display: 'flex', alignItems: 'center', gap: 8
                      }}
                    >
                      {isSearching ? (
                        <>
                          <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                          <span>Searching</span>
                        </>
                      ) : 'Search'}
                    </button>
                  </div>
                </div>
              </form>

              {/* Error display */}
              {errMsg && (
                <div className="error-box" style={{
                  marginTop: 16, padding: '12px 16px', borderRadius: 10, fontSize: 14
                }}>
                  <strong>Error: </strong>{errMsg}
                </div>
              )}
            </div>

            {/* Loading State */}
            {isSearching && (
              <div className="card animate-fade-in-up" style={{
                borderRadius: 16, padding: 48, textAlign: 'center'
              }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <p className="text-secondary" style={{ margin: 0 }}>
                  Searching across all documents...
                </p>
              </div>
            )}

            {/* 🎮 Space Invaders Easter Egg */}
            {showGame && !isSearching && (
              <div className="card animate-fade-in-up" style={{ borderRadius: 16, marginBottom: 24 }}>
                <SpaceInvaders
                  onClose={() => setShowGame(false)}
                  darkMode={darkMode}
                />
              </div>
            )}

            {/* Answer Card */}
            {answer && !isSearching && !showGame && (
              <div className="card animate-fade-in-up" style={{ borderRadius: 16, marginBottom: 24 }}>
                <div className="answer-header" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 16, padding: '16px 20px', borderBottom: '1px solid var(--border)'
                }}>
                  <h3 className="text-primary" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    Answer
                  </h3>

                  <div className="feedback-section" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="text-muted" style={{ fontSize: 13 }}>Was this helpful?</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleFeedback('up')}
                        className={`feedback-btn ${currentFeedback === 'up' ? 'active-up' : ''}`}
                        style={{ padding: '6px 12px', borderRadius: 8, fontSize: 16 }}
                        aria-label="Helpful"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => handleFeedback('down')}
                        className={`feedback-btn ${currentFeedback === 'down' ? 'active-down' : ''}`}
                        style={{ padding: '6px 12px', borderRadius: 8, fontSize: 16 }}
                        aria-label="Not helpful"
                      >
                        👎
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13 }}
                      onClick={() => navigator.clipboard.writeText(answer)}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div style={{ padding: 20 }}>
                  <pre className="answer-pre">{answer}</pre>

                  {/* Sources — only those the answer actually cited. */}
                  {(() => {
                    const citedSources = pickCitedSources(answer, sources);
                    if (citedSources.length === 0) return null;
                    return (
                      <div style={{
                        marginTop: 20, paddingTop: 16,
                        borderTop: '1px solid var(--border)'
                      }}>
                        <h4 className="text-muted" style={{
                          margin: '0 0 10px', fontSize: 12, fontWeight: 500,
                          textTransform: 'uppercase', letterSpacing: 0.5
                        }}>
                          Sources
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {citedSources.map(([key, src]) => (
                            <span
                              key={key}
                              className="text-accent"
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px', borderRadius: 6,
                                background: 'var(--bg-primary)', fontSize: 13
                              }}
                            >
                              [{key}] {src.title || `Source ${key}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Example Questions - show when no answer */}
            {!answer && !isSearching && !showGame && (
              <div className="animate-fade-in-up" style={{ marginBottom: 24 }}>
                <h3 className="text-secondary" style={{
                  margin: '0 0 12px', fontSize: 14, fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <span>💡</span> Try asking
                </h3>
                <div className="examples-grid">
                  {exampleQuestions.map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleExampleClick(example.text)}
                      className="example-btn"
                      style={{
                        borderRadius: 12, padding: 16, textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12
                      }}
                    >
                      <span style={{ fontSize: 24 }}>{example.icon}</span>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{example.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer style={{
          position: 'relative', zIndex: 10,
          borderTop: '1px solid var(--border)', padding: 24
        }}>
          <div className="container" style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: 16
          }}>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Press <kbd className="kbd" style={{ padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>/</kbd> to focus search
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Powered by <span className="text-secondary" style={{ fontWeight: 600 }}>UTM Healthcare</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
